import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Tag } from 'lucide-react';
import './BlogDetail.css';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const sectionRefs = useRef({});

  const fetchBlog = async () => {
    try {
      const response = await fetch(`/api/blogs/${id}`);
      const data = await response.json();
      setBlog(data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [id]);

  useEffect(() => {
    if (!blog) return;
    
    const template = blog.TemplateConfig ? JSON.parse(blog.TemplateConfig) : {};
    const sections = template.sections || [];
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      
      for (const section of sections) {
        const element = sectionRefs.current[section.id];
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [blog]);

  if (loading) {
    return (
      <div className="blog-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading blog...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail-error">
        <h2>Blog Not Found</h2>
        <button onClick={() => navigate('/blogs')} className="btn-back">
          Back to Blogs
        </button>
      </div>
    );
  }

  const template = blog.TemplateConfig ? JSON.parse(blog.TemplateConfig) : {};
  const { headerSize = 'large', textSize = 'medium', imagePosition = 'top', sections = [] } = template;

  const getHeaderSizeClass = () => {
    const sizes = {
      small: 'header-small',
      medium: 'header-medium',
      large: 'header-large',
      xlarge: 'header-xlarge'
    };
    return sizes[headerSize] || 'header-large';
  };

  const getTextSizeClass = () => {
    const sizes = {
      small: 'text-small',
      medium: 'text-medium',
      large: 'text-large'
    };
    return sizes[textSize] || 'text-medium';
  };

  const getFontSizeClass = (fontSize) => {
    const sizes = {
      small: 'font-small',
      medium: 'font-medium',
      large: 'font-large'
    };
    return sizes[fontSize] || 'font-medium';
  };

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const renderFormattedText = (text) => {
    if (!text) return null;
    
    // First apply formatting to the entire text, then split by lines
    const formattedContent = formatInlineText(text);
    
    // Now split the formatted content by newlines
    const lines = [];
    let currentLine = [];
    let lineKey = 0;
    
    formattedContent.forEach((element, index) => {
      if (typeof element === 'string') {
        // Split string elements by newlines
        const parts = element.split('\n');
        parts.forEach((part, partIndex) => {
          if (partIndex > 0) {
            // New line detected - push current line and start new one
            if (currentLine.length > 0) {
              const lineText = currentLine.map(el => typeof el === 'string' ? el : '').join('').trim();
              if (lineText.startsWith('•') || lineText.startsWith('\u2022')) {
                // Remove the bullet character from the content since CSS adds it
                const contentWithoutBullet = currentLine.map(el => 
                  typeof el === 'string' ? el.replace(/^[•\u2022]\s*/, '') : el
                );
                lines.push(<li key={lineKey++} className="formatted-bullet">{contentWithoutBullet}</li>);
              } else if (currentLine.some(el => el !== '')) {
                lines.push(<p key={lineKey++}>{currentLine}</p>);
              }
              currentLine = [];
            }
          }
          if (part) {
            currentLine.push(part);
          }
        });
      } else {
        // React element (bold/italic) - add to current line
        currentLine.push(element);
      }
    });
    
    // Push the last line
    if (currentLine.length > 0) {
      const lineText = currentLine.map(el => typeof el === 'string' ? el : '').join('').trim();
      if (lineText.startsWith('•') || lineText.startsWith('\u2022')) {
        // Remove the bullet character from the content since CSS adds it
        const contentWithoutBullet = currentLine.map(el => 
          typeof el === 'string' ? el.replace(/^[•\u2022]\s*/, '') : el
        );
        lines.push(<li key={lineKey++} className="formatted-bullet">{contentWithoutBullet}</li>);
      } else if (currentLine.some(el => el !== '')) {
        lines.push(<p key={lineKey++}>{currentLine}</p>);
      }
    }
    
    return lines;
  };

  const formatInlineText = (text) => {
    if (!text) return [text];
    
    // Process text to handle links, bold, and italic
    const elements = [];
    let keyCounter = 0;
    
    // First, split by link markers [text](url)
    const linkParts = text.split(/(\[[\s\S]*?\]\([\s\S]*?\))/g);
    
    linkParts.forEach((part) => {
      const linkMatch = part.match(/^\[([\s\S]*?)\]\(([\s\S]*?)\)$/);
      if (linkMatch) {
        // This is a link - extract text and URL
        const linkText = linkMatch[1];
        const linkUrl = linkMatch[2];
        elements.push(
          <a 
            key={`link-${keyCounter++}`} 
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="content-link"
          >
            {linkText}
          </a>
        );
      } else if (part) {
        // Not a link - check for bold
        const boldParts = part.split(/(\*\*[\s\S]*?\*\*)/g);
        
        boldParts.forEach((bPart) => {
          if (bPart.startsWith('**') && bPart.endsWith('**') && bPart.length > 4) {
            // This is bold text - remove the markers
            const boldText = bPart.slice(2, -2);
            elements.push(<strong key={`bold-${keyCounter++}`}>{boldText}</strong>);
          } else if (bPart) {
            // This is regular text - check for italic
            const italicParts = bPart.split(/(\*[\s\S]*?\*)/g);
            
            italicParts.forEach((iPart) => {
              if (iPart.startsWith('*') && iPart.endsWith('*') && iPart.length > 2 && !iPart.startsWith('**')) {
                // This is italic text - remove the markers
                const italicText = iPart.slice(1, -1);
                elements.push(<em key={`italic-${keyCounter++}`}>{italicText}</em>);
              } else if (iPart) {
                // Regular text
                elements.push(iPart);
              }
            });
          }
        });
      }
    });
    
    return elements.length > 0 ? elements : [text];
  };

  return (
    <div className="blog-detail-page">
      <div className="blog-detail-nav">
        <button onClick={() => navigate('/blogs')} className="btn-back">
          <ArrowLeft size={20} />
          Back to Blogs
        </button>
      </div>

      <div className="blog-layout-container">
        <div className="blog-main-column">
          <motion.div 
            className={`blog-hero ${getHeaderSizeClass()}`}
            style={{ backgroundColor: blog.Color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="blog-hero-content">
              <motion.div
                className="blog-meta"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <span className="meta-item">
                  <User size={18} />
                  {blog.Author}
                </span>
                <span className="meta-item">
                  <Tag size={18} />
                  {blog.Category}
                </span>
              </motion.div>
              
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {blog.Title}
              </motion.h1>
              
              <div className="blog-hero-description-wrapper">
                <motion.p
                  className="blog-description"
                  style={{ color: blog.DescriptionTextColor || '#FFFFFF' }}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {blog.Description}
                </motion.p>
                
                {blog.DescriptionImageFileName && (
                  <motion.div
                    className="blog-hero-image"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    <img 
                      src={`/uploads/${blog.DescriptionImageFileName}`} 
                      alt={blog.Title}
                    />
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          <div className="blog-main-content">
            {blog.ImageFileName && (
              <motion.div 
                className="blog-image-section"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <img 
                  src={`/uploads/${blog.ImageFileName}`} 
                  alt={blog.Title}
                  className="blog-main-image"
                />
              </motion.div>
            )}

            <div className={`blog-sections ${getTextSizeClass()}`}>
              {sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  ref={(el) => (sectionRefs.current[section.id] = el)}
                  className={`content-section section-image-${section.imagePosition || 'top'}`}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + (index * 0.1) }}
                >
                  {section.imageFileName && section.imagePosition === 'top' && (
                    <div 
                      className="section-image-wrapper"
                      style={{
                        marginLeft: `${section.imageOffsetX || 0}px`,
                        marginTop: `${section.imageOffsetY || 0}px`,
                        marginBottom: `${section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px`,
                        width: `${section.imageCustomWidth || 100}%`,
                        maxWidth: '100%'
                      }}
                    >
                      <img 
                        src={`/uploads/${section.imageFileName}`} 
                        alt={section.title || 'Section image'}
                        className="section-image"
                      />
                      {section.imageCaption && (
                        <p className="image-caption">{section.imageCaption}</p>
                      )}
                      {section.imageText && (
                        <div className="image-text">{renderFormattedText(section.imageText)}</div>
                      )}
                    </div>
                  )}

                  {section.title && (
                    <h2
                      style={{
                        backgroundColor: section.titleBackgroundColor && section.titleBackgroundColor !== 'transparent' ? section.titleBackgroundColor : 'transparent',
                        padding: section.titleBackgroundColor && section.titleBackgroundColor !== 'transparent' ? '16px 24px' : '0',
                        borderRadius: section.titleBackgroundColor && section.titleBackgroundColor !== 'transparent' ? '12px' : '0',
                        marginBottom: section.titleBackgroundColor && section.titleBackgroundColor !== 'transparent' ? '24px' : '20px',
                        color: section.titleTextColor || '#1D1D1F'
                      }}
                    >
                      {section.title}
                    </h2>
                  )}

                  <div className={`section-text-content ${section.imagePosition ? `image-pos-${section.imagePosition}` : ''}`}>
                    {section.imageFileName && ['top-left', 'center-left', 'bottom-left'].includes(section.imagePosition) && (
                      <div 
                        className={`section-image-wrapper section-image-${section.imagePosition}`}
                        style={{
                          marginLeft: `${section.imageOffsetX || 0}px`,
                          marginTop: `${section.imageOffsetY || 0}px`,
                          marginBottom: `${section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px`,
                          width: `${section.imageCustomWidth || 100}%`,
                          maxWidth: '100%'
                        }}
                      >
                        <img 
                          src={`/uploads/${section.imageFileName}`} 
                          alt={section.title || 'Section image'}
                          className="section-image"
                        />
                        {section.imageCaption && (
                          <p className="image-caption">{section.imageCaption}</p>
                        )}
                        {section.imageText && (
                          <div className="image-text">{renderFormattedText(section.imageText)}</div>
                        )}
                      </div>
                    )}

                    <div className="section-text">
                      {section.content && (
                        <div 
                          className={`section-content ${getFontSizeClass(section.fontSize)}`}
                          style={{ 
                            textAlign: section.textAlign || 'left',
                            lineHeight: section.lineSpacing === 'compact' ? '1.4' : 
                                       section.lineSpacing === 'relaxed' ? '2.0' : 
                                       section.lineSpacing === 'loose' ? '2.5' : '1.7'
                          }}
                        >
                          {renderFormattedText(section.content)}
                        </div>
                      )}
                    </div>

                    {section.imageFileName && ['top-right', 'center-right', 'bottom-right'].includes(section.imagePosition) && (
                      <div 
                        className={`section-image-wrapper section-image-${section.imagePosition}`}
                        style={{
                          marginLeft: `${section.imageOffsetX || 0}px`,
                          marginTop: `${section.imageOffsetY || 0}px`,
                          marginBottom: `${section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px`,
                          width: `${section.imageCustomWidth || 100}%`,
                          maxWidth: '100%'
                        }}
                      >
                        <img 
                          src={`/uploads/${section.imageFileName}`} 
                          alt={section.title || 'Section image'}
                          className="section-image"
                        />
                        {section.imageCaption && (
                          <p className="image-caption">{section.imageCaption}</p>
                        )}
                        {section.imageText && (
                          <div className="image-text">{renderFormattedText(section.imageText)}</div>
                        )}
                      </div>
                    )}
                  </div>

                  {section.imageFileName && section.imagePosition === 'bottom' && (
                    <div 
                      className="section-image-wrapper"
                      style={{
                        marginLeft: `${section.imageOffsetX || 0}px`,
                        marginTop: `${section.imageOffsetY || 0}px`,
                        marginBottom: `${section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px`,
                        width: `${section.imageCustomWidth || 100}%`,
                        maxWidth: '100%'
                      }}
                    >
                      <img 
                        src={`/uploads/${section.imageFileName}`} 
                        alt={section.title || 'Section image'}
                        className="section-image"
                      />
                      {section.imageCaption && (
                        <p className="image-caption">{section.imageCaption}</p>
                      )}
                      {section.imageText && (
                        <div className="image-text">{renderFormattedText(section.imageText)}</div>
                      )}
                    </div>
                  )}

                  {/* Multiple Images - Each with its own position */}
                  {section.images && section.images.length > 0 && section.images.map((image) => {
                    const imageStyle = {
                      marginLeft: `${image.offsetX || 0}px`,
                      marginTop: `${image.offsetY || 0}px`,
                      marginBottom: `${image.spaceBelow !== undefined ? image.spaceBelow : 32}px`,
                      width: `${image.customWidth || 100}%`,
                      maxWidth: '100%'
                    };

                    return (
                      <div key={image.id}>
                        {image.fileName && image.imagePosition === 'top' && (
                          <div className="section-image-wrapper" style={imageStyle}>
                            <img 
                              src={`/uploads/${image.fileName}`} 
                              alt={image.caption || section.title || 'Section image'}
                              className="section-image"
                            />
                            {image.caption && (
                              <p className="image-caption">{image.caption}</p>
                            )}
                            {image.imageText && (
                              <div className="image-text">{renderFormattedText(image.imageText)}</div>
                            )}
                          </div>
                        )}

                        {image.fileName && ['top-left', 'center-left', 'bottom-left', 'top-right', 'center-right', 'bottom-right'].includes(image.imagePosition) && (
                          <div className={`section-text-content ${image.imagePosition ? `image-pos-${image.imagePosition}` : ''}`}>
                            {['top-left', 'center-left', 'bottom-left'].includes(image.imagePosition) && (
                              <div className={`section-image-wrapper section-image-${image.imagePosition}`} style={imageStyle}>
                                <img 
                                  src={`/uploads/${image.fileName}`} 
                                  alt={image.caption || section.title || 'Section image'}
                                  className="section-image"
                                />
                                {image.caption && (
                                  <p className="image-caption">{image.caption}</p>
                                )}
                                {image.imageText && (
                                  <div className="image-text">{renderFormattedText(image.imageText)}</div>
                                )}
                              </div>
                            )}

                            <div className="section-text">
                              {/* Empty text div for layout */}
                            </div>

                            {['top-right', 'center-right', 'bottom-right'].includes(image.imagePosition) && (
                              <div className={`section-image-wrapper section-image-${image.imagePosition}`} style={imageStyle}>
                                <img 
                                  src={`/uploads/${image.fileName}`} 
                                  alt={image.caption || section.title || 'Section image'}
                                  className="section-image"
                                />
                                {image.caption && (
                                  <p className="image-caption">{image.caption}</p>
                                )}
                                {image.imageText && (
                                  <div className="image-text">{renderFormattedText(image.imageText)}</div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {image.fileName && image.imagePosition === 'bottom' && (
                          <div className="section-image-wrapper" style={imageStyle}>
                            <img 
                              src={`/uploads/${image.fileName}`} 
                              alt={image.caption || section.title || 'Section image'}
                              className="section-image"
                            />
                            {image.caption && (
                              <p className="image-caption">{image.caption}</p>
                            )}
                            {image.imageText && (
                              <div className="image-text">{renderFormattedText(image.imageText)}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {sections.length > 0 && (
          <aside className="blog-sidebar-column">
            <div className="table-of-contents">
              <div className="toc-sticky">
                <h3>CONTENTS</h3>
                <nav className="toc-nav">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      className={`toc-item ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      {section.title || `Section ${index + 1}`}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default BlogDetail;
